import { useState, useEffect } from "react";
import { ArrowUp, ArrowDown, Pencil, Plus, Trash2, CheckCircle, XCircle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { DEFAULT_THEME, DEFAULT_CONTENT, DEFAULT_NAV, DEFAULT_SEO, DEFAULT_CHECKOUT, DEFAULT_SECTIONS, FONT_OPTIONS } from "../../lib/theme";
import { ColorField, ImageField, TextField } from "../../components/admin/fields";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const TABS = ["Global", "Hero", "Sections", "Text", "Typography", "Colors", "Navigation", "SEO", "Checkout", "Integrations"];

const COLOR_FIELDS = [
  ["background_color", "Background"], ["text_color", "Primary Text"], ["secondary_text_color", "Secondary Text"],
  ["heading_color", "Headings"], ["secondary_color", "Gold / Accent"], ["primary_color", "Buttons"],
  ["button_text_color", "Button Text"], ["button_hover_color", "Button Hover"], ["border_color", "Borders"],
  ["card_color", "Cards"], ["price_color", "Price"], ["sale_price_color", "Sale Badge"],
  ["header_bg", "Header"], ["footer_bg", "Footer"], ["footer_text", "Footer Text"],
  ["announcement_bg", "Announcement Bar"], ["announcement_text", "Announcement Text"],
];

const PRESET_SELECTS = [
  ["heading_size", "Heading Size", [["small", "Small"], ["default", "Default"], ["large", "Large"]]],
  ["body_size", "Body Text Size", [["small", "Small"], ["default", "Default"], ["large", "Large"]]],
  ["button_size", "Button Text Size", [["small", "Small"], ["default", "Default"], ["large", "Large"]]],
  ["letter_spacing", "Letter Spacing", [["tight", "Tight"], ["default", "Default"], ["wide", "Wide"]]],
  ["line_height", "Line Height", [["compact", "Compact"], ["default", "Default"], ["relaxed", "Relaxed"]]],
];

const SEO_PAGES = ["home", "shop", "about", "wholesale", "contact", "faq"];

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-4 py-2" data-testid={`${testid}-row`}>
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

export default function AdminWebsiteControl() {
  const { refresh } = useTheme();
  const [tab, setTab] = useState("Global");
  const [data, setData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const s = r.data;
      setData({
        business_name: s.business_name || "Humaira Crafts",
        announcement: s.announcement || "",
        hero_title: s.hero_title || "",
        hero_subtitle: s.hero_subtitle || "",
        low_stock_threshold: s.low_stock_threshold ?? 5,
        theme: { ...DEFAULT_THEME, ...(s.theme || {}), sections: DEFAULT_SECTIONS.map((d) => (s.theme?.sections || []).find((x) => x.id === d.id) || d) },
        content: { ...DEFAULT_CONTENT, ...(s.content || {}) },
        nav: Array.isArray(s.nav) && s.nav.length ? s.nav.map((n) => ({ ...n })) : DEFAULT_NAV.map((n) => ({ ...n })),
        seo: { ...DEFAULT_SEO, ...(s.seo || {}) },
        checkout: { ...DEFAULT_CHECKOUT, ...(s.checkout || {}) },
      });
    }).catch(() => {});
    api.get("/admin/dashboard").then((r) => setIntegrations(r.data.integrations)).catch(() => {});
  }, []);

  if (!data) return <div className="h-40 bg-muted animate-pulse" />;

  const setTheme = (k, v) => setData((d) => ({ ...d, theme: { ...d.theme, [k]: v } }));
  const setContent = (k, v) => setData((d) => ({ ...d, content: { ...d.content, [k]: v } }));
  const setSeo = (k, v) => setData((d) => ({ ...d, seo: { ...d.seo, [k]: v } }));
  const setCheckout = (k, v) => setData((d) => ({ ...d, checkout: { ...d.checkout, [k]: v } }));
  const setNav = (nav) => setData((d) => ({ ...d, nav }));

  const moveSection = (i, dir) => {
    const next = [...data.theme.sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setTheme("sections", next);
  };

  const moveNav = (i, dir) => {
    const next = [...data.nav];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setNav(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        business_name: data.business_name,
        announcement: data.announcement,
        hero_title: data.hero_title,
        hero_subtitle: data.hero_subtitle,
        low_stock_threshold: parseInt(data.low_stock_threshold) || 5,
        theme: data.theme,
        content: data.content,
        nav: data.nav,
        seo: data.seo,
        checkout: data.checkout,
      });
      await refresh();
      toast.success("Website updated — changes are live");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const card = "bg-white border border-black/10 p-6";
  const secTitle = "small-caps text-gold mb-6";

  return (
    <div data-testid="admin-website-control">
      <h1 className="font-serif text-3xl font-semibold mb-2">Website Control</h1>
      <p className="text-sm text-muted-foreground mb-6">Control your entire website from here — no code needed. Click "Save & Publish" after making changes.</p>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="wc-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs uppercase tracking-widest whitespace-nowrap border transition-colors duration-300 ${tab === t ? "bg-[#111] text-[#FAF9F6] border-[#111]" : "border-black/15 hover:border-gold"}`}
            data-testid={`wc-tab-${t.toLowerCase()}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-6">
        {tab === "Global" && (
          <div className={card} data-testid="wc-global">
            <p className={secTitle}>Global Website Settings</p>
            <div className="grid md:grid-cols-2 gap-6">
              <TextField label="Website Name" value={data.business_name} onChange={(v) => setData({ ...data, business_name: v })} testid="wc-site-name" />
              <TextField label="Announcement Bar Text" value={data.announcement} onChange={(v) => setData({ ...data, announcement: v })} testid="wc-announcement" />
              <ImageField label="Logo" value={data.theme.logo_url} onChange={(v) => setTheme("logo_url", v)} testid="wc-logo" hint="Recommended: transparent PNG, ~300×80px" />
              <ImageField label="Favicon" value={data.theme.favicon_url} onChange={(v) => setTheme("favicon_url", v)} testid="wc-favicon" hint="Recommended: square, 64×64px" />
            </div>
            <div className="mt-6 border-t border-black/10 pt-4 max-w-md">
              <Toggle label="Announcement Bar ON/OFF" checked={data.theme.announcement_enabled} onChange={(v) => setTheme("announcement_enabled", v)} testid="wc-announcement-toggle" />
              <Toggle label="Header Visible" checked={data.theme.header_visible} onChange={(v) => setTheme("header_visible", v)} testid="wc-header-toggle" />
              <Toggle label="Footer Visible" checked={data.theme.footer_visible} onChange={(v) => setTheme("footer_visible", v)} testid="wc-footer-toggle" />
              <Toggle label="Maintenance Mode (store hidden, admin works)" checked={data.theme.maintenance_mode} onChange={(v) => setTheme("maintenance_mode", v)} testid="wc-maintenance-toggle" />
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <TextField label="Maintenance Message" value={data.content.maintenance_message} onChange={(v) => setContent("maintenance_message", v)} testid="wc-maintenance-msg" />
              <TextField label="Copyright Text ({year} = current year)" value={data.content.copyright_text} onChange={(v) => setContent("copyright_text", v)} testid="wc-copyright" />
              <TextField label="Low-Stock Warning Threshold" value={String(data.low_stock_threshold)} onChange={(v) => setData({ ...data, low_stock_threshold: v })} testid="wc-low-stock" />
            </div>
          </div>
        )}

        {tab === "Hero" && (
          <div className={card} data-testid="wc-hero">
            <p className={secTitle}>Hero Section & Background Image</p>
            <div className="grid md:grid-cols-2 gap-6">
              <ImageField label="Desktop Hero Image" value={data.theme.hero_image} onChange={(v) => setTheme("hero_image", v)} testid="wc-hero-desktop" hint="Recommended: 1920×1080px or larger, landscape" />
              <ImageField label="Mobile Hero Image (optional)" value={data.theme.hero_mobile_image} onChange={(v) => setTheme("hero_mobile_image", v)} testid="wc-hero-mobile" hint="Recommended: 800×1000px, portrait" />
              <div>
                <Label className="text-xs uppercase tracking-widest">Image Fit</Label>
                <Select value={data.theme.hero_fit} onValueChange={(v) => setTheme("hero_fit", v)}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="wc-hero-fit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cover" data-testid="fit-cover">Cover (fills, crops edges)</SelectItem>
                    <SelectItem value="contain" data-testid="fit-contain">Contain (full image visible)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Image Position</Label>
                <Select value={data.theme.hero_position} onValueChange={(v) => setTheme("hero_position", v)}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="wc-hero-position"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["center", "left", "right", "top", "bottom"].map((p) => <SelectItem key={p} value={p} data-testid={`pos-${p}`}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Overlay Darkness — {data.theme.hero_overlay}%</Label>
                <input type="range" min="0" max="80" value={data.theme.hero_overlay} onChange={(e) => setTheme("hero_overlay", parseInt(e.target.value))}
                  className="w-full mt-3 accent-[#D4AF37]" data-testid="wc-hero-overlay" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Section Height</Label>
                <Select value={data.theme.hero_height} onValueChange={(v) => setTheme("hero_height", v)}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="wc-hero-height"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small" data-testid="height-small">Small (50% screen)</SelectItem>
                    <SelectItem value="default" data-testid="height-default">Default (70-80% screen)</SelectItem>
                    <SelectItem value="large" data-testid="height-large">Large (90% screen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Layout</Label>
                <Select value={data.theme.hero_layout} onValueChange={(v) => setTheme("hero_layout", v)}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="wc-hero-layout"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left" data-testid="layout-left">Classic — glass card, left</SelectItem>
                    <SelectItem value="center" data-testid="layout-center">Centered card</SelectItem>
                    <SelectItem value="minimal" data-testid="layout-minimal">Minimal — text on image</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Text Alignment</Label>
                <Select value={data.theme.hero_text_align} onValueChange={(v) => setTheme("hero_text_align", v)}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="wc-hero-align"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["left", "center", "right"].map((a) => <SelectItem key={a} value={a} data-testid={`align-${a}`}>{a[0].toUpperCase() + a.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">Images are never stretched — fit and position control how the image fills the space without distortion.</p>
            <div className="border-t border-black/10 mt-6 pt-6 grid md:grid-cols-2 gap-6">
              <Toggle label="Hero Section Enabled" checked={data.theme.hero_enabled} onChange={(v) => setTheme("hero_enabled", v)} testid="wc-hero-enabled" />
              <div />
              <TextField label="Hero Heading" value={data.hero_title} onChange={(v) => setData({ ...data, hero_title: v })} testid="wc-hero-title" />
              <TextField label="Hero Paragraph" value={data.hero_subtitle} onChange={(v) => setData({ ...data, hero_subtitle: v })} testid="wc-hero-subtitle" />
              <TextField label="Primary Button Label" value={data.content.hero_primary_text} onChange={(v) => setContent("hero_primary_text", v)} testid="wc-hero-btn1" />
              <TextField label="Primary Button Link" value={data.content.hero_primary_link} onChange={(v) => setContent("hero_primary_link", v)} testid="wc-hero-btn1-link" />
              <TextField label="Secondary Button Label" value={data.content.hero_secondary_text} onChange={(v) => setContent("hero_secondary_text", v)} testid="wc-hero-btn2" />
              <TextField label="Secondary Button Link" value={data.content.hero_secondary_link} onChange={(v) => setContent("hero_secondary_link", v)} testid="wc-hero-btn2-link" />
            </div>
          </div>
        )}

        {tab === "Sections" && (
          <div className={card} data-testid="wc-sections">
            <p className={secTitle}>Homepage Sections — Enable, Reorder, Edit</p>
            <div className="flex flex-col divide-y divide-black/5">
              {data.theme.sections.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3 py-3" data-testid={`wc-section-${s.id}`}>
                  <Switch checked={s.visible} onCheckedChange={(v) => setTheme("sections", data.theme.sections.map((x) => x.id === s.id ? { ...x, visible: v } : x))} data-testid={`wc-section-toggle-${s.id}`} />
                  <span className="flex-1 text-sm">{s.label}</span>
                  <button onClick={() => setEditSection({ ...s, _i: i })} className="p-1.5 border border-black/10 hover:border-gold transition-colors duration-300" data-testid={`wc-section-edit-${s.id}`} aria-label="Edit"><Pencil strokeWidth={1.5} size={14} /></button>
                  <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1.5 border border-black/10 hover:border-gold disabled:opacity-30 transition-colors duration-300" data-testid={`wc-section-up-${s.id}`} aria-label="Up"><ArrowUp strokeWidth={1.5} size={14} /></button>
                  <button onClick={() => moveSection(i, 1)} disabled={i === data.theme.sections.length - 1} className="p-1.5 border border-black/10 hover:border-gold disabled:opacity-30 transition-colors duration-300" data-testid={`wc-section-down-${s.id}`} aria-label="Down"><ArrowDown strokeWidth={1.5} size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Text" && (
          <>
            <div className={card}>
              <p className={secTitle}>Homepage — Trust Bar & Stats</p>
              <div className="grid md:grid-cols-2 gap-4">
                {data.content.trust_items.map((item, i) => (
                  <div key={i} className="border border-black/10 p-4">
                    <TextField label={`Trust Item ${i + 1} — Title`} value={item.title} onChange={(v) => setContent("trust_items", data.content.trust_items.map((x, xi) => xi === i ? { ...x, title: v } : x))} testid={`wc-trust-title-${i}`} />
                    <div className="mt-3">
                      <TextField label="Text" value={item.text} onChange={(v) => setContent("trust_items", data.content.trust_items.map((x, xi) => xi === i ? { ...x, text: v } : x))} testid={`wc-trust-text-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {data.content.heritage_stats.map((s, i) => (
                  <div key={i} className="border border-black/10 p-4">
                    <TextField label={`Stat ${i + 1} Value`} value={s.value} onChange={(v) => setContent("heritage_stats", data.content.heritage_stats.map((x, xi) => xi === i ? { ...x, value: v } : x))} testid={`wc-stat-value-${i}`} />
                    <div className="mt-3">
                      <TextField label="Label" value={s.label} onChange={(v) => setContent("heritage_stats", data.content.heritage_stats.map((x, xi) => xi === i ? { ...x, label: v } : x))} testid={`wc-stat-label-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={card}>
              <p className={secTitle}>About / Our Story Page</p>
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Kicker (small top text)" value={data.content.about_hero_kicker} onChange={(v) => setContent("about_hero_kicker", v)} testid="wc-about-kicker" />
                <TextField label="Main Title" value={data.content.about_hero_title} onChange={(v) => setContent("about_hero_title", v)} testid="wc-about-title" />
                <TextField label="Section 1 Heading" value={data.content.about_s1_heading} onChange={(v) => setContent("about_s1_heading", v)} testid="wc-about-s1-heading" />
                <TextField label="Section 2 Heading" value={data.content.about_s2_heading} onChange={(v) => setContent("about_s2_heading", v)} testid="wc-about-s2-heading" />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <TextField textarea rows={4} label="Paragraph 1" value={data.content.about_s1_p1} onChange={(v) => setContent("about_s1_p1", v)} testid="wc-about-p1" />
                <TextField textarea rows={4} label="Paragraph 2" value={data.content.about_s1_p2} onChange={(v) => setContent("about_s1_p2", v)} testid="wc-about-p2" />
              </div>
              <div className="mt-4">
                <TextField textarea rows={6} label="What We Make — one item per line" value={data.content.about_s2_items} onChange={(v) => setContent("about_s2_items", v)} testid="wc-about-items" />
              </div>
            </div>

            <div className={card}>
              <p className={secTitle}>Wholesale Page</p>
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Kicker" value={data.content.wholesale_hero_kicker} onChange={(v) => setContent("wholesale_hero_kicker", v)} testid="wc-ws-kicker" />
                <TextField label="Title" value={data.content.wholesale_hero_title} onChange={(v) => setContent("wholesale_hero_title", v)} testid="wc-ws-title" />
                <TextField label="Benefits Heading" value={data.content.wholesale_benefits_heading} onChange={(v) => setContent("wholesale_benefits_heading", v)} testid="wc-ws-benefits-heading" />
                <TextField label="Form Heading" value={data.content.wholesale_form_heading} onChange={(v) => setContent("wholesale_form_heading", v)} testid="wc-ws-form-heading" />
              </div>
              <div className="mt-4">
                <TextField textarea rows={3} label="Intro Text" value={data.content.wholesale_hero_text} onChange={(v) => setContent("wholesale_hero_text", v)} testid="wc-ws-text" />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                {data.content.wholesale_benefits.map((b, i) => (
                  <div key={i} className="border border-black/10 p-4">
                    <TextField label={`Benefit ${i + 1} Title`} value={b.title} onChange={(v) => setContent("wholesale_benefits", data.content.wholesale_benefits.map((x, xi) => xi === i ? { ...x, title: v } : x))} testid={`wc-benefit-title-${i}`} />
                    <div className="mt-3">
                      <TextField label="Text" value={b.text} onChange={(v) => setContent("wholesale_benefits", data.content.wholesale_benefits.map((x, xi) => xi === i ? { ...x, text: v } : x))} testid={`wc-benefit-text-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={card}>
              <p className={secTitle}>Contact & Footer</p>
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Contact Page Heading" value={data.content.contact_heading} onChange={(v) => setContent("contact_heading", v)} testid="wc-contact-heading" />
                <TextField label="Footer Description Text" value={data.content.footer_text} onChange={(v) => setContent("footer_text", v)} testid="wc-footer-text" />
              </div>
            </div>

            <div className={card}>
              <p className={secTitle}>FAQ Editor</p>
              {data.content.faq_items.map((f, i) => (
                <div key={i} className="border border-black/10 p-4 mb-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <TextField label={`Question ${i + 1}`} value={f.q} onChange={(v) => setContent("faq_items", data.content.faq_items.map((x, xi) => xi === i ? { ...x, q: v } : x))} testid={`wc-faq-q-${i}`} />
                      <div className="mt-3">
                        <TextField textarea rows={2} label="Answer" value={f.a} onChange={(v) => setContent("faq_items", data.content.faq_items.map((x, xi) => xi === i ? { ...x, a: v } : x))} testid={`wc-faq-a-${i}`} />
                      </div>
                    </div>
                    <button onClick={() => setContent("faq_items", data.content.faq_items.filter((_, xi) => xi !== i))} className="p-2 hover:text-destructive transition-colors duration-300" data-testid={`wc-faq-del-${i}`} aria-label="Delete"><Trash2 strokeWidth={1.5} size={16} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => setContent("faq_items", [...data.content.faq_items, { q: "", a: "" }])} className="flex items-center gap-2 border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold transition-colors duration-300" data-testid="wc-faq-add">
                <Plus strokeWidth={1.5} size={14} /> Add Question
              </button>
            </div>

            <div className={card}>
              <p className={secTitle}>Policy Pages</p>
              <p className="text-[11px] text-muted-foreground mb-4">Start a line with "## " to make it a heading. Separate paragraphs with a blank line.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <TextField textarea rows={10} label="Privacy Policy" value={data.content.policy_privacy} onChange={(v) => setContent("policy_privacy", v)} testid="wc-policy-privacy" />
                <TextField textarea rows={10} label="Return & Refund Policy" value={data.content.policy_returns} onChange={(v) => setContent("policy_returns", v)} testid="wc-policy-returns" />
                <TextField textarea rows={10} label="Shipping Policy" value={data.content.policy_shipping} onChange={(v) => setContent("policy_shipping", v)} testid="wc-policy-shipping" />
                <TextField textarea rows={10} label="Terms & Conditions" value={data.content.policy_terms} onChange={(v) => setContent("policy_terms", v)} testid="wc-policy-terms" />
              </div>
            </div>
          </>
        )}

        {tab === "Typography" && (
          <div className={card} data-testid="wc-typography">
            <p className={secTitle}>Typography</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs uppercase tracking-widest">Heading Font</Label>
                <Select value={data.theme.heading_font} onValueChange={(v) => setTheme("heading_font", v)}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="wc-heading-font"><SelectValue /></SelectTrigger>
                  <SelectContent>{FONT_OPTIONS.heading.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Body Font</Label>
                <Select value={data.theme.body_font} onValueChange={(v) => setTheme("body_font", v)}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="wc-body-font"><SelectValue /></SelectTrigger>
                  <SelectContent>{FONT_OPTIONS.body.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {PRESET_SELECTS.map(([key, label, options]) => (
                <div key={key}>
                  <Label className="text-xs uppercase tracking-widest">{label}</Label>
                  <Select value={data.theme[key]} onValueChange={(v) => setTheme(key, v)}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid={`wc-${key.replace(/_/g, "-")}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${key}-${v}`}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Colors" && (
          <div className={card} data-testid="wc-colors">
            <p className={secTitle}>Theme Colors</p>
            <div className="grid md:grid-cols-2 gap-6">
              {COLOR_FIELDS.map(([key, label]) => (
                <ColorField key={key} label={label} value={data.theme[key]} onChange={(v) => setTheme(key, v)} testid={`wc-color-${key.replace(/_/g, "-")}`} />
              ))}
            </div>
          </div>
        )}

        {tab === "Navigation" && (
          <div className={card} data-testid="wc-navigation">
            <p className={secTitle}>Header Menu</p>
            {data.nav.map((n, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-black/5" data-testid={`wc-nav-row-${i}`}>
                <Switch checked={n.visible} onCheckedChange={(v) => setNav(data.nav.map((x, xi) => xi === i ? { ...x, visible: v } : x))} data-testid={`wc-nav-visible-${i}`} />
                <input value={n.label} onChange={(e) => setNav(data.nav.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                  className="border border-black/15 px-3 py-2 text-sm w-36 outline-none focus:border-gold" placeholder="Label" data-testid={`wc-nav-label-${i}`} />
                <input value={n.url} onChange={(e) => setNav(data.nav.map((x, xi) => xi === i ? { ...x, url: e.target.value } : x))}
                  className="border border-black/15 px-3 py-2 text-sm flex-1 outline-none focus:border-gold" placeholder="/page-url" data-testid={`wc-nav-url-${i}`} />
                <button onClick={() => moveNav(i, -1)} disabled={i === 0} className="p-1.5 border border-black/10 hover:border-gold disabled:opacity-30" data-testid={`wc-nav-up-${i}`} aria-label="Up"><ArrowUp strokeWidth={1.5} size={14} /></button>
                <button onClick={() => moveNav(i, 1)} disabled={i === data.nav.length - 1} className="p-1.5 border border-black/10 hover:border-gold disabled:opacity-30" data-testid={`wc-nav-down-${i}`} aria-label="Down"><ArrowDown strokeWidth={1.5} size={14} /></button>
                <button onClick={() => setNav(data.nav.filter((_, xi) => xi !== i))} className="p-1.5 border border-black/10 hover:text-destructive" data-testid={`wc-nav-del-${i}`} aria-label="Delete"><Trash2 strokeWidth={1.5} size={14} /></button>
              </div>
            ))}
            <button onClick={() => setNav([...data.nav, { label: "New Link", url: "/", visible: true }])} className="mt-4 flex items-center gap-2 border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold transition-colors duration-300" data-testid="wc-nav-add">
              <Plus strokeWidth={1.5} size={14} /> Add Menu Item
            </button>
            <div className="mt-6 border-t border-black/10 pt-4 max-w-md">
              <p className="small-caps text-gold mb-2">Header Icons</p>
              <Toggle label="Search" checked={data.theme.show_search} onChange={(v) => setTheme("show_search", v)} testid="wc-show-search" />
              <Toggle label="Wishlist" checked={data.theme.show_wishlist} onChange={(v) => setTheme("show_wishlist", v)} testid="wc-show-wishlist" />
              <Toggle label="Account" checked={data.theme.show_account} onChange={(v) => setTheme("show_account", v)} testid="wc-show-account" />
              <Toggle label="Cart" checked={data.theme.show_cart} onChange={(v) => setTheme("show_cart", v)} testid="wc-show-cart" />
            </div>
          </div>
        )}

        {tab === "SEO" && (
          <div className={card} data-testid="wc-seo">
            <p className={secTitle}>SEO — Page Titles & Descriptions</p>
            <p className="text-[11px] text-muted-foreground mb-4">Leave blank to use the built-in defaults. Product-level SEO is on each product's edit page.</p>
            <div className="mb-6 max-w-md border border-black/10 p-4">
              <Toggle label="Allow search engines to index this site (Google)" checked={data.seo.search_indexing !== false} onChange={(v) => setSeo("search_indexing", v)} testid="wc-search-indexing" />
              <p className="text-[11px] text-muted-foreground mt-2">Keep this ON. Turn it off only during maintenance — your pages will disappear from Google and Google Images while off.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {SEO_PAGES.map((p) => (
                <div key={p} className="border border-black/10 p-4">
                  <p className="text-xs uppercase tracking-widest text-gold mb-3">{p} page</p>
                  <TextField label="SEO Title" value={data.seo[`${p}_title`]} onChange={(v) => setSeo(`${p}_title`, v)} testid={`wc-seo-${p}-title`} />
                  <div className="mt-3">
                    <TextField textarea rows={2} label="Meta Description" value={data.seo[`${p}_description`]} onChange={(v) => setSeo(`${p}_description`, v)} testid={`wc-seo-${p}-desc`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <ImageField label="Open Graph Image (social sharing)" value={data.seo.og_image} onChange={(v) => setSeo("og_image", v)} testid="wc-og-image" hint="Recommended: 1200×630px" />
            </div>
          </div>
        )}

        {tab === "Checkout" && (
          <div className={card} data-testid="wc-checkout">
            <p className={secTitle}>Checkout & Payment Settings</p>
            <div className="max-w-md">
              <Toggle label="Guest Checkout (buy without account)" checked={data.checkout.guest_checkout_enabled} onChange={(v) => setCheckout("guest_checkout_enabled", v)} testid="wc-guest-checkout" />
              <Toggle label="Customer Registration" checked={data.checkout.registration_enabled} onChange={(v) => setCheckout("registration_enabled", v)} testid="wc-registration" />
              <Toggle label="Cash on Delivery (COD)" checked={data.checkout.cod_enabled} onChange={(v) => setCheckout("cod_enabled", v)} testid="wc-cod" />
              <Toggle label="Online Payment (Razorpay)" checked={data.checkout.online_enabled} onChange={(v) => setCheckout("online_enabled", v)} testid="wc-online-payment" />
              <Toggle label="Coupon System" checked={data.checkout.coupons_enabled} onChange={(v) => setCheckout("coupons_enabled", v)} testid="wc-coupons" />
              <Toggle label="Campaign / Offers System" checked={data.checkout.campaigns_enabled} onChange={(v) => setCheckout("campaigns_enabled", v)} testid="wc-campaigns" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">Customers can cancel orders while status is Placed or Confirmed. After shipping, cancellation is blocked automatically.</p>
          </div>
        )}

        {tab === "Integrations" && integrations && (
          <div className={card} data-testid="wc-integrations">
            <p className={secTitle}>Integration Status</p>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="border border-black/10 p-5" data-testid="status-razorpay">
                <div className="flex items-center gap-2">
                  {integrations.razorpay_configured ? <CheckCircle size={18} className="text-green-700" /> : <XCircle size={18} className="text-destructive" />}
                  <p className="font-medium text-sm">Razorpay</p>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {integrations.razorpay_configured ? `Connected · ${integrations.razorpay_mode.toUpperCase()} mode` : "Not configured — add API keys on the server"}
                </p>
              </div>
              <div className="border border-black/10 p-5" data-testid="status-shiprocket">
                <div className="flex items-center gap-2">
                  {integrations.shiprocket_configured ? <CheckCircle size={18} className="text-green-700" /> : <XCircle size={18} className="text-destructive" />}
                  <p className="font-medium text-sm">Shiprocket</p>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {integrations.shiprocket_configured ? "Connected — create shipments from Orders" : "Not configured — add API credentials on the server"}
                </p>
              </div>
              <div className="border border-black/10 p-5" data-testid="status-email">
                <div className="flex items-center gap-2">
                  {integrations.email_configured ? <CheckCircle size={18} className="text-green-700" /> : <XCircle size={18} className="text-destructive" />}
                  <p className="font-medium text-sm">Email Notifications</p>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{integrations.email_configured ? "Active" : "Not configured"}</p>
              </div>
              <div className="border border-black/10 p-5" data-testid="status-whatsapp">
                <div className="flex items-center gap-2">
                  <XCircle size={18} className="text-muted-foreground" />
                  <p className="font-medium text-sm">WhatsApp</p>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Not configured — can be connected later via an API service</p>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <button onClick={async () => {
                try {
                  const { data } = await api.get("/admin/integrations/test");
                  setTestResult(data);
                } catch (e) { toast.error(formatApiError(e)); }
              }} className="border border-black/20 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300" data-testid="test-integrations">
                Test Connections
              </button>
              {testResult && (
                <p className="text-xs" data-testid="test-results">
                  Razorpay: <span className={testResult.razorpay === "connected" ? "text-green-700" : "text-muted-foreground"}>{testResult.razorpay}</span> ·
                  Shiprocket: <span className={testResult.shiprocket === "connected" ? "text-green-700" : "text-muted-foreground"}> {testResult.shiprocket}</span> ·
                  Email: <span className={testResult.email === "connected" ? "text-green-700" : "text-muted-foreground"}> {testResult.email}</span>
                </p>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">
              For security, API keys and secrets are stored only on the server (never in the browser). To connect Razorpay or Shiprocket, share your credentials with your developer to add to the server configuration.
            </p>
          </div>
        )}

        <button onClick={save} disabled={saving}
          className="bg-[#111] text-[#FAF9F6] py-4 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50 sticky bottom-4"
          data-testid="wc-save">
          {saving ? "Publishing..." : "Save & Publish"}
        </button>
      </div>

      <Dialog open={!!editSection} onOpenChange={() => setEditSection(null)}>
        <DialogContent className="bg-[#FAF9F6] max-w-lg" data-testid="section-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit: {editSection?.label}</DialogTitle></DialogHeader>
          {editSection && (
            <div className="flex flex-col gap-4 mt-2">
              <TextField label="Heading (blank = default)" value={editSection.content?.heading || ""} onChange={(v) => setEditSection({ ...editSection, content: { ...editSection.content, heading: v } })} testid="sec-edit-heading" />
              <TextField label="Subheading" value={editSection.content?.subheading || ""} onChange={(v) => setEditSection({ ...editSection, content: { ...editSection.content, subheading: v } })} testid="sec-edit-subheading" />
              <TextField textarea rows={3} label="Paragraph" value={editSection.content?.paragraph || ""} onChange={(v) => setEditSection({ ...editSection, content: { ...editSection.content, paragraph: v } })} testid="sec-edit-paragraph" />
              <div className="grid grid-cols-2 gap-4">
                <TextField label="Button Text" value={editSection.content?.button_text || ""} onChange={(v) => setEditSection({ ...editSection, content: { ...editSection.content, button_text: v } })} testid="sec-edit-button-text" />
                <TextField label="Button Link" value={editSection.content?.button_link || ""} onChange={(v) => setEditSection({ ...editSection, content: { ...editSection.content, button_link: v } })} testid="sec-edit-button-link" />
              </div>
              <ColorField label="Section Background (click black square to clear via hex)" value={editSection.content?.bg_color || "#FAF9F6"} onChange={(v) => setEditSection({ ...editSection, content: { ...editSection.content, bg_color: v } })} testid="sec-edit-bg" />
              <div>
                <Label className="text-xs uppercase tracking-widest">Text Alignment</Label>
                <Select value={editSection.content?.text_align || ""} onValueChange={(v) => setEditSection({ ...editSection, content: { ...editSection.content, text_align: v } })}>
                  <SelectTrigger className="mt-2 rounded-sm bg-white" data-testid="sec-edit-align"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button onClick={() => {
                setTheme("sections", data.theme.sections.map((x, xi) => xi === editSection._i ? { ...x, content: editSection.content } : x));
                setEditSection(null);
                toast.success("Section updated — click Save & Publish");
              }} className="bg-[#111] text-[#FAF9F6] py-3 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300" data-testid="sec-edit-save">
                Apply
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
